import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, InputGroup, Badge, Spinner } from 'react-bootstrap';
import { collection, addDoc, serverTimestamp, getFirestore, getDocs, query, where } from 'firebase/firestore';
import firebaseApp from '../../../firebase/credenciales';
import { FaSave, FaTimes, FaPlus } from 'react-icons/fa';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import Swal from 'sweetalert2';

const PerfilForm = ({ perfil, onSave, onCancel, usuario, submitting }) => {
    const [newEspecialidad, setNewEspecialidad] = useState('');
    const [profesiones, setProfesiones] = useState([]);
    const [habilidades, setHabilidades] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [selectedHabilidades, setSelectedHabilidades] = useState([]);
    const [newHabilidad, setNewHabilidad] = useState('');
    const [newProfesion, setNewProfesion] = useState('');
    const [selectedProfesiones, setSelectedProfesiones] = useState([]);

    // Acceder a Firestore
    const db = getFirestore(firebaseApp);

    // Obtener RUC y nombre de la empresa del usuario autenticado
    const ruc = usuario?.ruc || '';
    const nombreEmpresa = usuario?.empresa || 'Empresa';

    // Cargar profesiones y habilidades disponibles
    useEffect(() => {
        const loadOptions = async () => {
            setLoadingOptions(true);
            try {
                // Cargar profesiones
                const profesionesRef = collection(db, 'rs_profesiones');
                const qProfesiones = query(
                    profesionesRef,
                    where('ruc', '==', ruc)
                );
                const profesionesSnapshot = await getDocs(qProfesiones);
                const profesionesData = profesionesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setProfesiones(profesionesData);

                // Cargar habilidades
                const habilidadesRef = collection(db, 'rs_habilidades');
                const qHabilidades = query(
                    habilidadesRef,
                    where('ruc', '==', ruc)
                );
                const habilidadesSnapshot = await getDocs(qHabilidades);
                const habilidadesData = habilidadesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setHabilidades(habilidadesData);

                // Si estamos editando, cargar las habilidades seleccionadas
                if (perfil && perfil.habilidades) {
                    setSelectedHabilidades(perfil.habilidades);
                }

                // Si estamos editando, cargar las profesiones seleccionadas
                if (perfil) {
                    if (perfil.profesiones && Array.isArray(perfil.profesiones)) {
                        // Si ya tiene formato de array de profesiones
                        setSelectedProfesiones(perfil.profesiones);
                    } else if (perfil.profesionId) {
                        // Migración de formato antiguo (un solo ID)
                        const profesion = profesionesData.find(p => p.id === perfil.profesionId);
                        if (profesion) {
                            setSelectedProfesiones([{
                                id: profesion.id,
                                nombre: profesion.nombre,
                                nivelEducativo: profesion.nivelEducativo
                            }]);
                        }
                    }
                }
            } catch (error) {
                console.error("Error cargando opciones:", error);
            } finally {
                setLoadingOptions(false);
            }
        };

        loadOptions();
    }, [ruc, perfil]);

    // Configurar validación con Yup
    const validationSchema = Yup.object({
        codigo: Yup.string()
            .required('El código del perfil es obligatorio')
            .max(20, 'No debe exceder los 20 caracteres'),
        nombre: Yup.string()
            .required('El nombre del perfil es obligatorio')
            .min(3, 'Debe tener al menos 3 caracteres')
            .max(100, 'No debe exceder los 100 caracteres'),
        nivelExperiencia: Yup.number()
            .required('El nivel de experiencia es obligatorio')
            .min(0, 'No puede ser negativo'),
        descripcion: Yup.string()
            .max(1000, 'La descripción no debe exceder los 1000 caracteres')
    });

    // Configurar Formik
    const formik = useFormik({
        initialValues: {
            codigo: perfil?.codigo || '',
            nombre: perfil?.nombre || '',
            nivelExperiencia: perfil?.nivelExperiencia || 0,
            especialidades: perfil?.especialidades || [],
            descripcion: perfil?.descripcion || '',
            funciones: perfil?.funciones || '',
            requisitosAdicionales: perfil?.requisitosAdicionales || '',
            activo: perfil?.activo !== false, // Por defecto activo=true
        },
        validationSchema,
        onSubmit: handleSubmit
    });

    // Función para manejar el guardado
    async function handleSubmit(values) {
        try {
            // Validar que haya al menos una profesión seleccionada
            if (selectedProfesiones.length === 0) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error de validación',
                    text: 'Debe seleccionar al menos una profesión para el perfil'
                });
                return;
            }

            // No necesitamos setSubmitting(true) aquí, ya lo maneja el componente padre

            // Preparar datos para guardar
            const perfilData = {
                ...values,
                profesiones: selectedProfesiones,
                profesionPrincipal: selectedProfesiones.length > 0 ? selectedProfesiones[0].nombre : '',
                habilidades: selectedHabilidades,
                ruc,
                empresa: nombreEmpresa
            };

            // Si es un nuevo perfil
            if (!perfil) {
                perfilData.fechaCreacion = serverTimestamp();
                perfilData.usos = 0;
            }

            // Para compatibilidad con código existente que podría esperar profesionId
            if (selectedProfesiones.length > 0) {
                perfilData.profesionId = selectedProfesiones[0].id;
                perfilData.profesionNombre = selectedProfesiones[0].nombre;
            }

            // Enviar datos al componente padre para guardar
            onSave(perfilData);
        } catch (error) {
            console.error("Error en el formulario:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Ocurrió un error al procesar el formulario.'
            });

            // No necesitamos setSubmitting(false) aquí, ya lo maneja el componente padre
        }
    }

    // Función para agregar especialidad
    const handleAddEspecialidad = () => {
        if (newEspecialidad.trim() && !formik.values.especialidades.includes(newEspecialidad.trim())) {
            formik.setFieldValue('especialidades', [...formik.values.especialidades, newEspecialidad.trim()]);
            setNewEspecialidad('');
        }
    };

    // Función para eliminar especialidad
    const handleRemoveEspecialidad = (especialidad) => {
        formik.setFieldValue('especialidades', formik.values.especialidades.filter(e => e !== especialidad));
    };

    // Función para agregar una habilidad
    const handleAddHabilidad = () => {
        if (!newHabilidad) return;

        // Buscar la habilidad seleccionada
        const habilidad = habilidades.find(h => h.id === newHabilidad);
        if (habilidad && !selectedHabilidades.some(h => h.id === habilidad.id)) {
            setSelectedHabilidades([...selectedHabilidades, {
                id: habilidad.id,
                nombre: habilidad.nombre,
                categoria: habilidad.categoria || ''
            }]);
            setNewHabilidad('');
        }
    };

    // Función para eliminar una habilidad
    const handleRemoveHabilidad = (habilidadId) => {
        setSelectedHabilidades(selectedHabilidades.filter(h => h.id !== habilidadId));
    };

    // Función para agregar una profesión
    const handleAddProfesion = () => {
        if (!newProfesion) return;

        // Buscar la profesión seleccionada
        const profesion = profesiones.find(p => p.id === newProfesion);
        if (profesion && !selectedProfesiones.some(p => p.id === profesion.id)) {
            setSelectedProfesiones([...selectedProfesiones, {
                id: profesion.id,
                nombre: profesion.nombre,
                nivelEducativo: profesion.nivelEducativo
            }]);
            setNewProfesion('');
        }
    };

    // Función para eliminar una profesión
    const handleRemoveProfesion = (profesionId) => {
        setSelectedProfesiones(selectedProfesiones.filter(p => p.id !== profesionId));
    };

    return (
        <Form onSubmit={formik.handleSubmit}>
            <Row className="mb-3">
                <Col md={4}>
                    <Form.Group controlId="codigo">
                        <Form.Label>Código *</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ej: TEC-01"
                            {...formik.getFieldProps('codigo')}
                            isInvalid={formik.touched.codigo && !!formik.errors.codigo}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.codigo}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={8}>
                    <Form.Group controlId="nombre">
                        <Form.Label>Nombre del Perfil *</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ej: Técnico Instrumentista I"
                            {...formik.getFieldProps('nombre')}
                            isInvalid={formik.touched.nombre && !!formik.errors.nombre}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.nombre}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Profesiones Requeridas *</Form.Label>
                <InputGroup>
                    <Form.Select
                        value={newProfesion}
                        onChange={(e) => setNewProfesion(e.target.value)}
                        disabled={loadingOptions}
                    >
                        <option value="">Seleccionar profesión</option>
                        {profesiones
                            .filter(p => !selectedProfesiones.some(sp => sp.id === p.id))
                            .map(profesion => (
                                <option key={profesion.id} value={profesion.id}>
                                    {profesion.nombre} ({profesion.nivelEducativo})
                                </option>
                            ))
                        }
                    </Form.Select>
                    <Button
                        variant="outline-primary"
                        onClick={handleAddProfesion}
                        disabled={!newProfesion || loadingOptions}
                    >
                        <FaPlus />
                    </Button>
                </InputGroup>
                <Form.Text className="text-muted mb-2 d-block">
                    Seleccione una o más profesiones válidas para este perfil. La primera seleccionada será la principal.
                </Form.Text>

                {selectedProfesiones.length === 0 ? (
                    <div className="alert alert-warning">
                        <small>Debe seleccionar al menos una profesión para el perfil</small>
                    </div>
                ) : (
                    <div className="d-flex flex-wrap gap-2 mt-2">
                        {selectedProfesiones.map((profesion, index) => (
                            <Badge
                                key={profesion.id}
                                bg={index === 0 ? "primary" : "info"}
                                className="d-flex align-items-center p-2"
                            >
                                {profesion.nombre}
                                {index === 0 && <span className="ms-1">(Principal)</span>}
                                <Button
                                    variant="link"
                                    className="p-0 ms-2 text-white"
                                    onClick={() => handleRemoveProfesion(profesion.id)}
                                >
                                    <FaTimes size={12} />
                                </Button>
                            </Badge>
                        ))}
                    </div>
                )}
            </Form.Group>

            <Form.Group className="mb-4" controlId="nivelExperiencia">
                <Form.Label>Experiencia Mínima (años) *</Form.Label>
                <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    {...formik.getFieldProps('nivelExperiencia')}
                    isInvalid={formik.touched.nivelExperiencia && !!formik.errors.nivelExperiencia}
                />
                <Form.Control.Feedback type="invalid">
                    {formik.errors.nivelExperiencia}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Especialidades</Form.Label>
                <InputGroup>
                    <Form.Control
                        type="text"
                        placeholder="Ej: Automatización Industrial"
                        value={newEspecialidad}
                        onChange={(e) => setNewEspecialidad(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEspecialidad())}
                    />
                    <Button
                        variant="outline-primary"
                        onClick={handleAddEspecialidad}
                    >
                        <FaPlus />
                    </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                    Ingrese especialidades específicas para este perfil y presione el botón para agregar.
                </Form.Text>

                <div className="mt-2">
                    {formik.values.especialidades.length === 0 ? (
                        <span className="text-muted">No hay especialidades agregadas</span>
                    ) : (
                        <div className="d-flex flex-wrap gap-2">
                            {formik.values.especialidades.map((especialidad, index) => (
                                <Badge
                                    key={index}
                                    bg="light"
                                    text="dark"
                                    className="d-flex align-items-center p-2"
                                >
                                    {especialidad}
                                    <Button
                                        variant="link"
                                        className="p-0 ms-2 text-danger"
                                        onClick={() => handleRemoveEspecialidad(especialidad)}
                                    >
                                        <FaTimes size={12} />
                                    </Button>
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Habilidades y Competencias Requeridas</Form.Label>
                <InputGroup>
                    <Form.Select
                        value={newHabilidad}
                        onChange={(e) => setNewHabilidad(e.target.value)}
                        disabled={loadingOptions}
                    >
                        <option value="">Seleccionar habilidad</option>
                        {habilidades
                            .filter(h => !selectedHabilidades.some(sh => sh.id === h.id))
                            .map(habilidad => (
                                <option key={habilidad.id} value={habilidad.id}>
                                    {habilidad.nombre} {habilidad.categoria ? `(${habilidad.categoria})` : ''}
                                </option>
                            ))
                        }
                    </Form.Select>
                    <Button
                        variant="outline-primary"
                        onClick={handleAddHabilidad}
                        disabled={!newHabilidad || loadingOptions}
                    >
                        <FaPlus />
                    </Button>
                </InputGroup>

                <div className="mt-2">
                    {loadingOptions ? (
                        <div className="text-center py-2">
                            <Spinner size="sm" animation="border" /> Cargando...
                        </div>
                    ) : selectedHabilidades.length === 0 ? (
                        <span className="text-muted">No hay habilidades seleccionadas</span>
                    ) : (
                        <div className="d-flex flex-wrap gap-2">
                            {selectedHabilidades.map((habilidad) => (
                                <Badge
                                    key={habilidad.id}
                                    bg={habilidad.categoria ? "info" : "secondary"}
                                    className="d-flex align-items-center p-2"
                                >
                                    {habilidad.nombre}
                                    <Button
                                        variant="link"
                                        className="p-0 ms-2 text-white"
                                        onClick={() => handleRemoveHabilidad(habilidad.id)}
                                    >
                                        <FaTimes size={12} />
                                    </Button>
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
            </Form.Group>

            <Form.Group className="mb-3" controlId="descripcion">
                <Form.Label>Descripción del Perfil</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Descripción general del perfil profesional"
                    {...formik.getFieldProps('descripcion')}
                    isInvalid={formik.touched.descripcion && !!formik.errors.descripcion}
                />
                <Form.Control.Feedback type="invalid">
                    {formik.errors.descripcion}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="funciones">
                <Form.Label>Funciones Principales</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Liste las principales funciones y responsabilidades de este perfil"
                    {...formik.getFieldProps('funciones')}
                />
            </Form.Group>

            <Form.Group className="mb-3" controlId="requisitosAdicionales">
                <Form.Label>Requisitos Adicionales</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Otros requisitos específicos para este perfil"
                    {...formik.getFieldProps('requisitosAdicionales')}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Check
                    type="switch"
                    id="activoSwitch"
                    label="Perfil activo"
                    checked={formik.values.activo}
                    onChange={() => formik.setFieldValue('activo', !formik.values.activo)}
                />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
                <Button
                    variant="outline-secondary"
                    onClick={onCancel}
                    disabled={submitting}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    type="submit"
                    disabled={submitting || selectedProfesiones.length === 0}
                    className="d-flex align-items-center"
                >
                    {submitting ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Guardando...
                        </>
                    ) : (
                        <>
                            <FaSave className="me-2" />
                            Guardar Perfil
                        </>
                    )}
                </Button>
            </div>
        </Form>
    );
};

export default PerfilForm;